import { useState, type ReactNode } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Link,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import LanguageIcon from '@mui/icons-material/Language';
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import { extractErrorMessage, resources } from '../../../core/services';
import { ConfirmDialog } from '../../../shared/components/Dialogs/ConfirmDialog';
import { DescriptionStep } from '../shared/DescriptionStep';
import { isValidHttpUrl, normalizeUrl } from '../shared/SourcesStep';
import type { OnboardingDraft } from '../shared/useOnboardingDraft';
import { ideaValidationCopy as copy } from './copy';

const MAX_SOURCES_SHOWN = 5;

/** Switch value for starting from an empty editor. Not a template label: it maps to `templateLabel: ''`. */
const BLANK_KIND = 'blank';

/** A blank start, then the story templates offered as a switch, matched by `ProjectGeneratePromptTemplate.label` in the seeded catalog. */
const PRODUCT_KINDS: { value: string; name: string; icon: ReactNode }[] = [
  { value: BLANK_KIND, name: 'Blank', icon: <InsertDriveFileOutlinedIcon fontSize="small" /> },
  { value: 'web-app-saas', name: 'Web Application', icon: <LanguageIcon fontSize="small" /> },
  { value: 'mobile-app', name: 'Mobile App', icon: <PhoneIphoneIcon fontSize="small" /> },
];

interface IdeaStepProps {
  draft: OnboardingDraft;
  onChange: (patch: Partial<OnboardingDraft>) => void;
}

/**
 * The idea editor with a Web Application / Mobile App / Blank switch that
 * fills in the matching story template or empties the editor, and, below
 * it, the option to draft the description from an existing website in the
 * structure of the chosen template. All replace the text, so all ask first
 * when the founder has written something of their own.
 */
export function IdeaStep({ draft, onChange }: IdeaStepProps) {
  const [pending, setPending] = useState<{ message: string; onConfirm: () => void } | null>(null);
  const [urlTouched, setUrlTouched] = useState(false);

  const templatesQuery = useQuery({
    queryKey: ['projectGeneratePromptTemplates'],
    queryFn: () => resources.projectGeneratePromptTemplates.list({ pageSize: 100, sort: 'order' }),
  });
  const templates = templatesQuery.data?.data ?? [];
  const templateFor = (label: string) => templates.find((t) => t.label === label) ?? null;

  const generate = useMutation({
    mutationFn: (websiteUrl: string) =>
      resources.generateIdeaDescription({ websiteUrl, templateLabel: draft.templateLabel || undefined }),
    onSuccess: (result) => onChange({ description: result.description, websiteUrl: result.websiteUrl }),
  });

  // Text worth protecting: anything but an untouched template.
  const hasOwnText =
    draft.description.trim().length > 0 && draft.description !== templateFor(draft.templateLabel)?.content;

  // No template picked means a blank start, so Blank stays selected until a template is.
  const selectedKind = draft.templateLabel || BLANK_KIND;
  const applyKind = (kind: string) => {
    if (kind === BLANK_KIND) {
      onChange({ description: '', templateLabel: '' });
      return;
    }
    const template = templateFor(kind);
    if (template) onChange({ description: template.content, templateLabel: kind });
  };
  const pickKind = (kind: string | null) => {
    if (!kind || kind === selectedKind) return;
    const message = kind === BLANK_KIND ? copy.kind.clearMessage : copy.kind.replaceMessage;
    if (hasOwnText) setPending({ message, onConfirm: () => applyKind(kind) });
    else applyKind(kind);
  };

  const websiteUrl = draft.websiteUrl;
  const urlValid = isValidHttpUrl(websiteUrl);
  const urlError =
    urlTouched && websiteUrl.trim() && !urlValid ? 'Enter a full address such as https://example.com' : null;

  const startGeneration = () => {
    const run = () => generate.mutate(normalizeUrl(websiteUrl));
    if (hasOwnText) setPending({ message: copy.fromWebsite.replaceMessage, onConfirm: run });
    else run();
  };

  const kindSwitch = (
    <ToggleButtonGroup
      exclusive
      size="small"
      value={selectedKind}
      onChange={(_, next: string | null) => pickKind(next)}
      aria-label={copy.kind.label}
    >
      {PRODUCT_KINDS.map((kind) => (
        <ToggleButton
          key={kind.value}
          value={kind.value}
          disabled={kind.value !== BLANK_KIND && (templatesQuery.isLoading || !templateFor(kind.value))}
          sx={{ gap: 0.75, px: 1.5 }}
        >
          {kind.icon}
          <Typography variant="caption" fontWeight={600}>
            {kind.name}
          </Typography>
        </ToggleButton>
      ))}
    </ToggleButtonGroup>
  );

  return (
    <Stack spacing={3}>
      <DescriptionStep
        value={draft.description}
        onChange={(description) => onChange({ description })}
        heading={copy.idea.heading}
        helper={copy.idea.helper}
        label={copy.idea.label}
        placeholder={copy.idea.placeholder}
        required
        toolbarStart={kindSwitch}
      />

      <Box
        sx={{
          p: 2,
          borderRadius: 'var(--radius)',
          border: '1px solid',
          borderColor: 'divider',
          bgcolor: 'var(--color-surface-2)',
        }}
      >
        <Typography variant="subtitle2" fontWeight={700}>
          {copy.fromWebsite.heading}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 1.5 }}>
          {copy.fromWebsite.helper}
        </Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'flex-start' }}>
          <TextField
            label={copy.fromWebsite.urlLabel}
            type="url"
            inputMode="url"
            autoComplete="url"
            placeholder="https://"
            fullWidth
            value={websiteUrl}
            disabled={generate.isPending}
            onChange={(e) => {
              onChange({ websiteUrl: e.target.value });
              if (!generate.isIdle) generate.reset();
            }}
            onBlur={() => {
              setUrlTouched(true);
              onChange({ websiteUrl: normalizeUrl(websiteUrl) });
            }}
            onKeyDown={(e) => {
              // Enter here drafts the description; it must not submit the wizard.
              if (e.key !== 'Enter') return;
              e.preventDefault();
              if (urlValid && !generate.isPending) startGeneration();
            }}
            error={urlError !== null}
            helperText={urlError ?? ' '}
          />
          <Button
            type="button"
            variant="outlined"
            startIcon={generate.isPending ? <CircularProgress size={16} color="inherit" /> : <AutoAwesomeIcon />}
            disabled={!urlValid || generate.isPending}
            onClick={startGeneration}
            sx={{ flexShrink: 0, whiteSpace: 'nowrap', mt: { sm: '2px' } }}
          >
            {copy.fromWebsite.button}
          </Button>
        </Stack>
        {generate.isPending && (
          <Typography variant="caption" color="text.secondary">
            {copy.fromWebsite.generating}
          </Typography>
        )}
        {generate.isError && (
          <Alert severity="error" sx={{ mt: 1 }} onClose={() => generate.reset()}>
            {extractErrorMessage(generate.error)}
          </Alert>
        )}
        {generate.isSuccess && (
          <Box>
            <Typography variant="caption" color="success.main" fontWeight={600}>
              {copy.fromWebsite.done}
            </Typography>
            {generate.data.sources.length > 0 && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                {copy.fromWebsite.sourcesLabel}{' '}
                {generate.data.sources.slice(0, MAX_SOURCES_SHOWN).map((s, i) => (
                  <span key={s.url}>
                    {i > 0 && ', '}
                    <Link href={s.url} target="_blank" rel="noopener noreferrer">
                      {s.title}
                    </Link>
                  </span>
                ))}
              </Typography>
            )}
          </Box>
        )}
      </Box>

      <ConfirmDialog
        open={pending !== null}
        title="Replace your text?"
        message={pending?.message ?? ''}
        confirmLabel="Replace"
        onConfirm={() => {
          pending?.onConfirm();
          setPending(null);
        }}
        onClose={() => setPending(null)}
      />
    </Stack>
  );
}
